import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import {
  Box, Button, HStack, Text, Input, InputGroup, InputLeftElement, InputRightElement, IconButton,
  Table, Thead, Tbody, Tr, Th, Td, Menu, MenuButton, MenuList, MenuItem, MenuDivider,
  Select, Spinner, Tooltip, useDisclosure, Badge, Portal
} from '@chakra-ui/react'
import {
  FaCloudUploadAlt, FaFolderPlus, FaSearch, FaTimes, FaEllipsisV, FaDownload, FaExternalLinkAlt,
  FaPen, FaArrowsAlt, FaTrash, FaUserFriends, FaSortAmountDown, FaSortAmountUp, FaHome, FaChevronRight
} from 'react-icons/fa'
import moment from 'moment'
import useUIContext from 'hooks/ui-context'
import { USER_TYPES } from 'constants'
import { theme } from 'styles'
import {
  getStorageMe, getStorageUsage, browseStorage, searchStorage,
  createStorageFolder, renameStorageFolder, deleteStorageFolder,
  getStorageDownloadUrl, renameStorageFile, deleteStorageFile, moveStorageFile, moveStorageFolder
} from 'services/storage'
import { formatBytes, extensionOf, typeLabel, ItemIcon, canOpenInline, errorOf } from './storageUtils'
import useUploadQueue from './useUploadQueue'
import UploadQueue from './UploadQueue'
import NameModal from './NameModal'
import MoveModal from './MoveModal'
import AccessModal from './AccessModal'

const DRAG_TYPE = 'text/x-paloma-storage-item'

const SORTS = [
  { value: 'name', label: 'Name' },
  { value: 'type', label: 'Type' },
  { value: 'size', label: 'Size' },
  { value: 'date', label: 'Date' }
]

const sortItems = (items, sort) => {
  const dir = sort.dir === 'asc' ? 1 : -1
  const cmpText = (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true })
  return [...items].sort((a, b) => {
    let r = 0
    if (sort.key === 'size') r = (a.size || 0) - (b.size || 0)
    else if (sort.key === 'date') r = new Date(a.updated_at || a.created_at) - new Date(b.updated_at || b.created_at)
    else if (sort.key === 'type') r = cmpText(extensionOf(a.name), extensionOf(b.name))
    if (r === 0) r = cmpText(a.name, b.name)
    return r * dir
  })
}

const FileStorage = () => {
  const user = useSelector(state => state.user.currentUser)
  const isAdmin = user && user.user_type === USER_TYPES.ADMIN
  const [searchParams, setSearchParams] = useSearchParams()
  const folderId = searchParams.get('folder') || null
  const { setBreadcrumbs, addNotification, showConfirmModal } = useUIContext()

  const [meta, setMeta] = useState(null)
  const [usage, setUsage] = useState(null)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' })
  const [query, setQuery] = useState('')
  const [results, setResults] = useState(null)
  const [searching, setSearching] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [dropTarget, setDropTarget] = useState(null)
  const dragDepth = useRef(0)
  const fileInputRef = useRef()
  const folderIdRef = useRef(folderId)
  folderIdRef.current = folderId

  const [nameModal, setNameModal] = useState(null) // { mode, item }
  const [moveItem, setMoveItem] = useState(null)
  const [accessFolder, setAccessFolder] = useState(null)
  const nameDisclosure = useDisclosure()
  const moveDisclosure = useDisclosure()
  const accessDisclosure = useDisclosure()

  const dashboardPath = isAdmin ? '/admin/dashboard' : `/${user ? user.user_type : 'client'}/dashboard`

  const refreshUsage = useCallback(() => {
    if (!isAdmin) return
    getStorageUsage().then(res => { if (!errorOf(res)) setUsage(res.data) })
  }, [isAdmin])

  const load = useCallback(async (id) => {
    setLoading(true)
    const res = await browseStorage(id)
    // Ignore a response for a folder we have already navigated away from.
    if (folderIdRef.current !== id) return
    const err = errorOf(res, 'Could not load this folder')
    if (err) {
      setLoadError(res && res.status === 403 ? "You don't have access to this folder." : err)
      setData(null)
    } else {
      setLoadError(null)
      setData(res.data)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    getStorageMe().then(res => { if (!errorOf(res)) setMeta(res.data) })
    refreshUsage()
  }, [refreshUsage])

  useEffect(() => {
    load(folderId)
    setQuery('')
    setResults(null)
  }, [folderId, load])

  useEffect(() => {
    const crumbs = [
      { path: dashboardPath, label: 'Dashboard' },
      { path: '/files', label: 'File Storage' }
    ]
    if (data && data.breadcrumbs) {
      data.breadcrumbs.forEach(b => crumbs.push({ path: `/files?folder=${b._id}`, label: b.name }))
    }
    setBreadcrumbs(crumbs)
  }, [data, dashboardPath]) // eslint-disable-line react-hooks/exhaustive-deps

  // Search is global (every folder the user can see), debounced.
  useEffect(() => {
    const q = query.trim()
    if (!q) { setResults(null); setSearching(false); return }
    setSearching(true)
    const t = setTimeout(async () => {
      const res = await searchStorage(q)
      if (!errorOf(res)) setResults(res.data)
      setSearching(false)
    }, 300)
    return () => clearTimeout(t)
  }, [query])

  const currentFolderName = data && data.folder ? data.folder.name : 'Home'

  const { uploads, enqueue, cancel, clearFinished, activeCount } = useUploadQueue({
    limits: meta ? { maxBytes: meta.max_upload_bytes, allowed: meta.allowed_extensions } : null,
    onUploaded: (file, targetFolderId) => {
      if ((targetFolderId || null) === (folderIdRef.current || null)) load(folderIdRef.current)
      refreshUsage()
    }
  })

  const openFolder = (id) => {
    if (id) setSearchParams({ folder: id })
    else setSearchParams({})
  }

  const canEdit = Boolean(data && data.can_edit)

  const startUpload = (files) => {
    if (!canEdit || !files || !files.length) return
    const count = enqueue(files, folderId, currentFolderName)
    if (count === 0 && files.length) {
      addNotification({ status: 'error', content: 'None of those files can be uploaded. Check the upload panel for details.' })
    }
  }

  // --- file drop (upload) -------------------------------------------------
  const hasFiles = (e) => Array.from((e.dataTransfer && e.dataTransfer.types) || []).includes('Files')
  const onDragEnter = (e) => { if (!hasFiles(e) || !canEdit) return; e.preventDefault(); dragDepth.current += 1; setDragOver(true) }
  const onDragOver = (e) => { if (!hasFiles(e) || !canEdit) return; e.preventDefault(); e.dataTransfer.dropEffect = 'copy' }
  const onDragLeave = (e) => { if (!hasFiles(e)) return; dragDepth.current -= 1; if (dragDepth.current <= 0) { dragDepth.current = 0; setDragOver(false) } }
  const onDrop = (e) => {
    if (!hasFiles(e)) return
    e.preventDefault()
    dragDepth.current = 0
    setDragOver(false)
    if (!canEdit) return
    startUpload(e.dataTransfer.files)
  }

  // --- row drag (move) ----------------------------------------------------
  const onItemDragStart = (e, item) => {
    e.dataTransfer.setData(DRAG_TYPE, JSON.stringify({ kind: item.kind, _id: item._id, name: item.name }))
    e.dataTransfer.effectAllowed = 'move'
  }
  const isItemDrag = (e) => Array.from((e.dataTransfer && e.dataTransfer.types) || []).includes(DRAG_TYPE)
  const onTargetDragOver = (e, targetId) => {
    if (!isItemDrag(e) || !canEdit) return
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = 'move'
    setDropTarget(targetId === null ? 'home' : targetId)
  }
  const onTargetDragLeave = () => setDropTarget(null)
  const onTargetDrop = async (e, targetId) => {
    if (!isItemDrag(e)) return
    e.preventDefault()
    e.stopPropagation()
    setDropTarget(null)
    let payload
    try { payload = JSON.parse(e.dataTransfer.getData(DRAG_TYPE)) } catch (err) { return }
    if (payload.kind === 'folder' && String(payload._id) === String(targetId)) return
    if (String(targetId || '') === String(folderId || '')) return
    const res = payload.kind === 'folder'
      ? await moveStorageFolder(payload._id, targetId)
      : await moveStorageFile(payload._id, targetId)
    const err = errorOf(res, 'Could not move')
    if (err) addNotification({ status: 'error', content: err })
    else {
      addNotification({ status: 'success', content: `Moved “${payload.name}”` })
      load(folderId)
    }
  }

  // --- actions -------------------------------------------------------------
  const download = async (file, inline = false) => {
    const res = await getStorageDownloadUrl(file._id, inline ? 'inline' : 'attachment')
    const err = errorOf(res, 'Could not get a download link')
    if (err) { addNotification({ status: 'error', content: err }); return }
    if (inline) window.open(res.data.url, '_blank', 'noopener')
    else window.location.assign(res.data.url)
  }

  const openNameModal = (mode, item = null) => { setNameModal({ mode, item }); nameDisclosure.onOpen() }

  const nameModalProps = useMemo(() => {
    if (!nameModal) return {}
    const { mode, item } = nameModal
    if (mode === 'new-folder') {
      return {
        title: `New folder in ${currentFolderName}`,
        label: 'Folder name',
        initialValue: '',
        confirmLabel: 'Create',
        onSubmit: async (name) => {
          const res = await createStorageFolder(name, folderId)
          const err = errorOf(res, 'Could not create folder')
          if (err) return err
          addNotification({ status: 'success', content: `Created “${name}”` })
          load(folderId)
          refreshUsage()
        }
      }
    }
    const isFolder = mode === 'rename-folder'
    return {
      title: isFolder ? 'Rename folder' : 'Rename file',
      label: 'Name',
      initialValue: item.name,
      confirmLabel: 'Rename',
      selectBaseName: !isFolder,
      onSubmit: async (name) => {
        const res = isFolder ? await renameStorageFolder(item._id, name) : await renameStorageFile(item._id, name)
        const err = errorOf(res, 'Could not rename')
        if (err) return err
        load(folderId)
      }
    }
  }, [nameModal, currentFolderName, folderId]) // eslint-disable-line react-hooks/exhaustive-deps

  const confirmDelete = (item) => {
    const isFolder = item.kind === 'folder'
    showConfirmModal({
      title: isFolder ? 'Delete folder?' : 'Delete file?',
      content: isFolder
        ? `“${item.name}” and everything inside it will be permanently deleted. This cannot be undone.`
        : `“${item.name}” will be permanently deleted. This cannot be undone.`,
      buttons: [
        { label: 'Cancel', action: 'cancel' },
        { label: 'Delete', action: 'delete', color: 'red' }
      ],
      callback: async (action) => {
        if (action !== 'delete') return
        const res = isFolder ? await deleteStorageFolder(item._id) : await deleteStorageFile(item._id)
        const err = errorOf(res, 'Could not delete')
        if (err) addNotification({ status: 'error', content: err })
        else {
          addNotification({ status: 'success', content: `Deleted “${item.name}”` })
          load(folderId)
          refreshUsage()
          if (results) setResults(r => r && ({
            folders: r.folders.filter(f => f._id !== item._id),
            files: r.files.filter(f => f._id !== item._id)
          }))
        }
      }
    })
  }

  // --- rows ---------------------------------------------------------------
  const folderRows = useMemo(() => sortItems((data ? data.folders : []).map(f => ({ ...f, kind: 'folder' })), sort), [data, sort])
  const fileRows = useMemo(() => sortItems((data ? data.files : []).map(f => ({ ...f, kind: 'file' })), sort), [data, sort])
  const resultRows = useMemo(() => results
    ? [
        ...results.folders.map(f => ({ ...f, kind: 'folder' })),
        ...results.files.map(f => ({ ...f, kind: 'file' }))
      ]
    : [], [results])

  const uploaderName = (file) => {
    const u = file.uploaded_by
    if (!u) return ''
    return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || ''
  }

  // Plain render functions, not components: a component type created
  // inside render is new every time, and React would remount it (closing an
  // open menu) on every upload-progress tick.
  const renderActions = (item, inSearch) => {
    const isFolder = item.kind === 'folder'
    const editable = inSearch ? isAdmin : canEdit
    return (
      <Menu placement="bottom-end" isLazy>
        <MenuButton
          as={IconButton} size="sm" variant="ghost" icon={<FaEllipsisV />}
          aria-label={`Actions for ${item.name}`} onClick={e => e.stopPropagation()}
        />
        {/* Portal: the list box clips overflow, and without a portal the
            dropdown for the last rows is cut off or lands under the header. */}
        <Portal>
        <MenuList fontSize="sm" zIndex={25} onClick={e => e.stopPropagation()}>
          {isFolder && <MenuItem icon={<FaChevronRight />} onClick={() => openFolder(item._id)}>Open</MenuItem>}
          {!isFolder && <MenuItem icon={<FaDownload />} onClick={() => download(item)}>Download</MenuItem>}
          {!isFolder && canOpenInline(item.name) && (
            <MenuItem icon={<FaExternalLinkAlt />} onClick={() => download(item, true)}>Open in new tab</MenuItem>
          )}
          {inSearch && !isFolder && (
            <MenuItem icon={<FaFolderPlus />} onClick={() => openFolder(item.folder)}>Go to folder</MenuItem>
          )}
          {editable && (
            <>
              <MenuDivider />
              <MenuItem icon={<FaPen />} onClick={() => openNameModal(isFolder ? 'rename-folder' : 'rename-file', item)}>Rename</MenuItem>
              <MenuItem icon={<FaArrowsAlt />} onClick={() => { setMoveItem(item); moveDisclosure.onOpen() }}>Move</MenuItem>
              {isFolder && isAdmin && (
                <MenuItem icon={<FaUserFriends />} onClick={() => { setAccessFolder(item); accessDisclosure.onOpen() }}>Share / access</MenuItem>
              )}
              <MenuItem icon={<FaTrash />} color={theme.danger} onClick={() => confirmDelete(item)}>Delete</MenuItem>
            </>
          )}
        </MenuList>
        </Portal>
      </Menu>
    )
  }

  const renderRow = (item, inSearch = false) => {
    const isFolder = item.kind === 'folder'
    const isTarget = isFolder && dropTarget === item._id
    return (
      <Tr
        key={item._id}
        data-testid={`storage-row-${item.kind}`}
        cursor={isFolder ? 'pointer' : 'default'}
        _hover={{ bg: theme.greenTint }}
        bg={isTarget ? theme.greenTint : undefined}
        outline={isTarget ? `2px solid ${theme.green}` : undefined}
        outlineOffset="-2px"
        onClick={() => isFolder && openFolder(item._id)}
        onDoubleClick={() => !isFolder && download(item)}
        draggable={!inSearch && canEdit}
        onDragStart={e => onItemDragStart(e, item)}
        onDragOver={isFolder ? (e => onTargetDragOver(e, item._id)) : undefined}
        onDragLeave={isFolder ? onTargetDragLeave : undefined}
        onDrop={isFolder ? (e => onTargetDrop(e, item._id)) : undefined}
      >
        <Td overflow="hidden">
          <HStack spacing={3} minW={0}>
            <ItemIcon item={item} />
            <Box minW={0}>
              <Text fontWeight="600" noOfLines={1} title={item.name} color={theme.ink}>{item.name}</Text>
              {inSearch && (
                <Text fontSize="xs" color={theme.muted} noOfLines={1}>
                  {['Home', ...(item.path || [])].join(' / ')}
                </Text>
              )}
            </Box>
          </HStack>
        </Td>
        <Td color={theme.text} whiteSpace="nowrap" display={{ base: 'none', md: 'table-cell' }}>{typeLabel(item)}</Td>
        <Td color={theme.text} whiteSpace="nowrap" isNumeric display={{ base: 'none', sm: 'table-cell' }}>{isFolder ? '—' : formatBytes(item.size)}</Td>
        <Td color={theme.text} whiteSpace="nowrap" display={{ base: 'none', lg: 'table-cell' }}>
          <Tooltip label={isFolder ? '' : `Uploaded ${moment(item.created_at).format('MMM D, YYYY h:mm A')}${uploaderName(item) ? ` by ${uploaderName(item)}` : ''}`}>
            <Text as="span">{moment(item.updated_at || item.created_at).format('MMM D, YYYY')}</Text>
          </Tooltip>
        </Td>
        <Td textAlign="right" px={2}>{renderActions(item, inSearch)}</Td>
      </Tr>
    )
  }

  const renderCrumb = ({ id, label, isLast, icon }) => (
    <HStack spacing={1} key={id === null ? 'home' : id}>
      <Button
        size="sm" variant="ghost" px={2} fontWeight={isLast ? '700' : '500'}
        color={isLast ? theme.ink : theme.green}
        leftIcon={icon}
        onClick={() => !isLast && openFolder(id)}
        bg={dropTarget === (id === null ? 'home' : id) ? theme.greenTint : undefined}
        outline={dropTarget === (id === null ? 'home' : id) ? `2px solid ${theme.green}` : undefined}
        onDragOver={e => onTargetDragOver(e, id)}
        onDragLeave={onTargetDragLeave}
        onDrop={e => onTargetDrop(e, id)}
        isDisabled={false}
      >
        {label}
      </Button>
      {!isLast && <FaChevronRight size={10} color={theme.faint} />}
    </HStack>
  )

  const showSearch = Boolean(query.trim())
  const isEmpty = !showSearch && data && folderRows.length === 0 && fileRows.length === 0

  return (
    <Box
      w={{ base: '100%', xl: '88%', '2xl': '80%' }} mx="auto" px={{ base: 4, md: 6 }} pb={24}
      position="relative"
      onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
      minH="60vh"
    >
      {/* Header */}
      <HStack justify="space-between" align="flex-end" flexWrap="wrap" gap={3} mb={4}>
        <Box>
          <Text fontSize="2xl" fontWeight="bold" color={theme.ink}>File Storage</Text>
          {isAdmin && usage && (
            <Text fontSize="sm" color={theme.muted}>
              {formatBytes(usage.used_bytes)} of {formatBytes(usage.limit_bytes)} used
              {' · '}{usage.file_count} file{usage.file_count === 1 ? '' : 's'} in {usage.folder_count} folder{usage.folder_count === 1 ? '' : 's'}
              {' · '}up to {formatBytes(usage.max_upload_bytes)} per file
            </Text>
          )}
          {!isAdmin && (
            <Text fontSize="sm" color={theme.muted}>Folders that have been shared with you.</Text>
          )}
        </Box>
        <HStack spacing={2}>
          {canEdit && (
            <>
              <Button size="sm" variant="outline" leftIcon={<FaFolderPlus />} onClick={() => openNameModal('new-folder')}>
                New folder
              </Button>
              <Button size="sm" colorScheme="green" leftIcon={<FaCloudUploadAlt />} onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                Upload
              </Button>
              <input
                ref={fileInputRef} type="file" multiple hidden data-testid="storage-file-input"
                onChange={e => { startUpload(e.target.files); e.target.value = '' }}
              />
            </>
          )}
        </HStack>
      </HStack>

      {/* Toolbar */}
      <HStack mb={3} spacing={3} flexWrap="wrap" gap={2}>
        <InputGroup size="sm" maxW="340px">
          <InputLeftElement pointerEvents="none"><FaSearch color={theme.faint} /></InputLeftElement>
          <Input
            placeholder="Search all files and folders"
            value={query} onChange={e => setQuery(e.target.value)}
            borderRadius="md" bg="white"
            data-testid="storage-search"
          />
          {query && (
            <InputRightElement>
              <IconButton size="xs" variant="ghost" aria-label="Clear search" icon={<FaTimes />} onClick={() => setQuery('')} />
            </InputRightElement>
          )}
        </InputGroup>
        <HStack spacing={1} ml="auto">
          <Text fontSize="sm" color={theme.muted}>Sort</Text>
          <Select size="sm" w="110px" value={sort.key} onChange={e => setSort(s => ({ ...s, key: e.target.value }))} aria-label="Sort by" bg="white" borderRadius="md">
            {SORTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Select>
          <IconButton
            size="sm" variant="ghost" aria-label={sort.dir === 'asc' ? 'Ascending' : 'Descending'}
            icon={sort.dir === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />}
            onClick={() => setSort(s => ({ ...s, dir: s.dir === 'asc' ? 'desc' : 'asc' }))}
          />
        </HStack>
      </HStack>

      {/* Location */}
      {!showSearch && data && (
        <HStack spacing={0} mb={2} flexWrap="wrap" data-testid="storage-path">
          {(isAdmin || !data.breadcrumbs.length) && (
            renderCrumb({ id: null, label: 'Home', icon: <FaHome />, isLast: !data.breadcrumbs.length })
          )}
          {data.breadcrumbs.map((b, i) => renderCrumb({ id: b._id, label: b.name, isLast: i === data.breadcrumbs.length - 1 }))}
          {data.role && data.role !== 'admin' && (
            <Badge ml={2} colorScheme={data.role === 'editor' ? 'green' : 'gray'} textTransform="capitalize">{data.role}</Badge>
          )}
        </HStack>
      )}
      {showSearch && (
        <Text fontSize="sm" color={theme.muted} mb={2}>
          {searching ? 'Searching…' : `${resultRows.length} result${resultRows.length === 1 ? '' : 's'} for “${query.trim()}”`}
        </Text>
      )}

      {/* Listing. overflow:clip (not hidden) so this never becomes a scroll
          container: focusing a row menu near the bottom would otherwise
          scroll the box internally and cut off the table header. The columns
          collapse on small screens, so no scrolling wrapper is needed. */}
      <Box borderWidth="1px" borderColor={theme.line} borderRadius="12px" bg="white" overflow="clip" position="relative">
        {loading && !data && <Box py={16} textAlign="center"><Spinner size="lg" color={theme.green} /></Box>}
        {loadError && (
          <Box py={16} textAlign="center">
            <Text color={theme.danger} mb={3}>{loadError}</Text>
            <Button size="sm" variant="outline" onClick={() => openFolder(null)}>Back to File Storage</Button>
          </Box>
        )}
        {!loadError && (data || showSearch) && (
          <Table size="sm" variant="simple" style={{ tableLayout: 'fixed' }}>
              <Thead bg={theme.line2} sx={{ 'th:first-of-type': { borderTopLeftRadius: '12px' }, 'th:last-of-type': { borderTopRightRadius: '12px' } }}>
                <Tr>
                  <Th>Name</Th>
                  <Th w="110px" display={{ base: 'none', md: 'table-cell' }}>Type</Th>
                  <Th w="100px" isNumeric display={{ base: 'none', sm: 'table-cell' }}>Size</Th>
                  <Th w="150px" display={{ base: 'none', lg: 'table-cell' }}>Modified</Th>
                  <Th w="56px" />
                </Tr>
              </Thead>
              <Tbody>
                {showSearch
                  ? resultRows.map(item => renderRow(item, true))
                  : [...folderRows, ...fileRows].map(item => renderRow(item))}
              </Tbody>
            </Table>
        )}
        {!loadError && showSearch && !searching && resultRows.length === 0 && (
          <Text py={12} textAlign="center" color={theme.muted}>Nothing matches “{query.trim()}”.</Text>
        )}
        {isEmpty && (
          <Box py={12} textAlign="center" color={theme.muted} data-testid="storage-empty">
            {!data.folder && !isAdmin && !data.role
              ? <Text>No folders have been shared with you yet.</Text>
              : <>
                  <Text>This folder is empty.</Text>
                  {canEdit && <Text fontSize="sm" mt={1}>Drag files here or use the Upload button.</Text>}
                </>}
          </Box>
        )}
        {dragOver && canEdit && (
          <Box
            position="absolute" inset={0} bg="rgba(241,247,243,0.92)" zIndex={5}
            borderWidth="2px" borderStyle="dashed" borderColor={theme.green} borderRadius="12px"
            display="flex" alignItems="center" justifyContent="center" pointerEvents="none"
          >
            <HStack color={theme.green} fontWeight="700" spacing={3}>
              <FaCloudUploadAlt size={28} />
              <Text>Drop to upload to {currentFolderName}</Text>
            </HStack>
          </Box>
        )}
      </Box>

      {canEdit && (
        <Text mt={2} fontSize="xs" color={theme.faint}>
          Tip: drag a file or folder onto another folder (or a path link above) to move it.
        </Text>
      )}

      <UploadQueue uploads={uploads} activeCount={activeCount} onCancel={cancel} onClear={clearFinished} />

      <NameModal isOpen={nameDisclosure.isOpen} onClose={nameDisclosure.onClose} {...nameModalProps} />
      <MoveModal
        isOpen={moveDisclosure.isOpen} onClose={moveDisclosure.onClose} item={moveItem} isAdmin={isAdmin}
        onMoved={(moved) => { addNotification({ status: 'success', content: `Moved “${moved.name}”` }); load(folderId) }}
      />
      <AccessModal isOpen={accessDisclosure.isOpen} onClose={accessDisclosure.onClose} folder={accessFolder} />
    </Box>
  )
}

export default FileStorage
